import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Save, Info } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';

const Step7Residual = ({ data, onNext, onPrevious, onSaveAndExit, saving }) => {
  const [formData, setFormData] = useState({
    residual_method: data.residual_method || 'single',
    residual_beneficiary: data.residual_beneficiary || { name: '', relationship: '' },
    residual_distribution: data.residual_distribution || []
  });

  const inputClass = "bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 focus:ring-amber-500/20";
  const selectClass = "bg-slate-800 border-slate-700 text-white w-full p-2 rounded-md focus:border-amber-500";
  const labelClass = "text-slate-300 font-medium text-sm";

  const relationshipOptions = [
    'Spouse',
    'Son',
    'Daughter',
    'Father',
    'Mother',
    'Brother',
    'Sister',
    'Grandson',
    'Granddaughter',
    'Nephew',
    'Niece',
    'Uncle',
    'Aunt',
    'Cousin',
    'Partner',
    'Friend',
    'Charity/Organization',
    'Other'
  ];

  // Get beneficiaries from data for the "same as beneficiaries" option
  const beneficiaries = data.beneficiaries || [];

  const handleMethodChange = (method) => {
    setFormData(prev => ({
      ...prev,
      residual_method: method,
      residual_distribution: method === 'beneficiaries' 
        ? beneficiaries.map(b => ({ name: b.name, percentage: b.share_percentage || '' }))
        : []
    }));
  };

  const updateDistribution = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      residual_distribution: prev.residual_distribution.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onNext(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8" data-testid="step7-form">
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-white border-b border-slate-700 pb-2">Residual Assets</h3>
        
        {/* Info Box */}
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 flex gap-3">
          <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-300">
            <p className="font-medium mb-1">What are Residual Assets?</p>
            <p className="text-blue-300/80">
              These are any assets that may have been unintentionally left out of your Will, 
              or assets you acquire after making this Will. Specifying a residual beneficiary 
              ensures nothing is left unallocated.
            </p>
          </div>
        </div>

        {/* Method Selection */}
        <div className="space-y-4">
          <Label className="text-white font-medium">How would you like to distribute residual assets?</Label>
          
          <div className="space-y-3">
            <label className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
              formData.residual_method === 'single' 
                ? 'bg-amber-500/10 border-amber-500' 
                : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
            }`}>
              <input
                type="radio"
                name="residual_method"
                value="single"
                checked={formData.residual_method === 'single'}
                onChange={() => handleMethodChange('single')}
                className="w-4 h-4 text-amber-500 mt-1"
              />
              <div>
                <span className="text-white font-medium">Single Beneficiary</span>
                <p className="text-sm text-slate-400 mt-1">All residual assets go to one person</p>
              </div>
            </label>

            {beneficiaries.length > 0 && (
              <label className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                formData.residual_method === 'beneficiaries' 
                  ? 'bg-amber-500/10 border-amber-500' 
                  : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
              }`}>
                <input
                  type="radio"
                  name="residual_method"
                  value="beneficiaries"
                  checked={formData.residual_method === 'beneficiaries'}
                  onChange={() => handleMethodChange('beneficiaries')}
                  className="w-4 h-4 text-amber-500 mt-1"
                />
                <div>
                  <span className="text-white font-medium">Same as Primary Beneficiaries</span>
                  <p className="text-sm text-slate-400 mt-1">Distribute among existing beneficiaries</p>
                </div>
              </label>
            )}
          </div>
        </div>

        {/* Single Beneficiary Form */}
        {formData.residual_method === 'single' && (
          <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700 space-y-4">
            <h4 className="text-white font-medium">Residual Beneficiary</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={labelClass}>Full Name *</Label>
                <Input
                  type="text"
                  value={formData.residual_beneficiary.name}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    residual_beneficiary: { ...prev.residual_beneficiary, name: e.target.value }
                  }))}
                  required
                  className={inputClass}
                />
              </div>
              <div className="space-y-2">
                <Label className={labelClass}>Relationship *</Label>
                <select
                  value={formData.residual_beneficiary.relationship}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    residual_beneficiary: { ...prev.residual_beneficiary, relationship: e.target.value }
                  }))}
                  required
                  className={selectClass}
                >
                  <option value="">Select</option>
                  {relationshipOptions.map(rel => (
                    <option key={rel} value={rel}>{rel}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Distribution Among Beneficiaries */}
        {formData.residual_method === 'beneficiaries' && formData.residual_distribution.length > 0 && (
          <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700 space-y-4">
            <h4 className="text-white font-medium">Distribution Percentages</h4>
            <p className="text-sm text-slate-400">Adjust the percentage for each beneficiary if needed</p>
            
            {formData.residual_distribution.map((item, index) => (
              <div key={index} className="flex items-center gap-4">
                <span className="text-slate-300 flex-1">{item.name}</span>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={item.percentage}
                    onChange={(e) => updateDistribution(index, 'percentage', e.target.value)}
                    min="0"
                    max="100"
                    step="0.01"
                    className={`${inputClass} w-24`}
                  />
                  <span className="text-slate-400">%</span>
                </div>
              </div>
            ))}

            <div className={`p-3 rounded-lg ${
              formData.residual_distribution.reduce((sum, item) => sum + (parseFloat(item.percentage) || 0), 0) === 100
                ? 'bg-emerald-500/10 text-emerald-400'
                : 'bg-amber-500/10 text-amber-400'
            }`}>
              <p className="text-sm font-medium">
                Total: {formData.residual_distribution.reduce((sum, item) => sum + (parseFloat(item.percentage) || 0), 0).toFixed(2)}%
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex flex-wrap justify-between gap-4 pt-6 border-t border-slate-700">
        <div className="flex gap-2">
          <Button type="button" onClick={onPrevious} variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800">
            <ChevronLeft size={16} className="mr-2" /> Previous
          </Button>
          <Button type="button" onClick={() => onSaveAndExit(formData)} disabled={saving} variant="outline" className="border-emerald-600 text-emerald-400 hover:bg-emerald-500/10">
            <Save size={16} className="mr-2" /> Save & Exit
          </Button>
        </div>
        <Button type="submit" disabled={saving} className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold">
          Next: Special Wishes <ChevronRight size={16} className="ml-2" />
        </Button>
      </div>
    </form>
  );
};

export default Step7Residual;
